import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Alert,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Grid,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  List,
  ListItem,
  ListItemText,
  ToggleButtonGroup,
  ToggleButton,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Delete as DeleteIcon,
  Settings as SettingsIcon,
  CheckCircle as CheckCircleIcon,
  CloudUpload as CloudUploadIcon,
  Refresh as RefreshIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material';
import {
  kubeconfigApi,
  KubeconfigMetadata,
  KubeconfigUploadRequest,
  KubeconfigValidationResult,
} from '../services/integrations-api';

export const KubeconfigManager: React.FC = () => {
  const [kubeconfigs, setKubeconfigs] = useState<KubeconfigMetadata[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedKubeconfig, setSelectedKubeconfig] = useState<KubeconfigMetadata | null>(null);
  const [kubeconfigContent, setKubeconfigContent] = useState<string>('');
  const [validation, setValidation] = useState<KubeconfigValidationResult | null>(null);

  const [uploadForm, setUploadForm] = useState<KubeconfigUploadRequest>({
    name: '',
    kubeconfigContent: '',
    setAsDefault: false,
  });
  const [uploadMode, setUploadMode] = useState<'paste' | 'file'>('paste');

  useEffect(() => {
    loadKubeconfigs();
  }, []);

  const loadKubeconfigs = async () => {
    try {
      setLoading(true);
      const data = await kubeconfigApi.list();
      setKubeconfigs(data);
    } catch (err: any) {
      setError(err.message || 'Failed to load kubeconfigs');
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async () => {
    try {
      setLoading(true);
      setError(null);

      await kubeconfigApi.upload(uploadForm);

      setSuccess('Kubeconfig uploaded successfully!');
      setUploadDialogOpen(false);
      resetUploadForm();

      // Reload list
      await loadKubeconfigs();
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Upload failed');
    } finally {
      setLoading(false);
    }
  };

  const resetUploadForm = () => {
    setUploadForm({
      name: '',
      kubeconfigContent: '',
      setAsDefault: false,
    });
    setValidation(null);
    setUploadMode('paste');
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      const content = await file.text();
      setUploadForm({
        ...uploadForm,
        kubeconfigContent: content,
        name: uploadForm.name || file.name.replace(/\.(yaml|yml|config)$/i, '')
      });
      setSuccess(`File loaded: ${file.name}`);
    } catch (err: any) {
      setError(`Failed to read file: ${err.message}`);
    }
  };

  const handleValidate = async () => {
    if (!uploadForm.kubeconfigContent) {
      setError('Please enter kubeconfig content first');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const result = await kubeconfigApi.validate(uploadForm.kubeconfigContent);
      setValidation(result);

      if (result.valid) {
        setSuccess('Kubeconfig is valid!');
      } else {
        setError(`Validation failed: ${result.errors.join(', ')}`);
      }
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Validation failed');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this kubeconfig?')) {
      return;
    }

    try {
      setLoading(true);
      setError(null);

      await kubeconfigApi.delete(id);

      setSuccess('Kubeconfig deleted successfully');
      await loadKubeconfigs();
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Delete failed');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async (id: string) => {
    try {
      setLoading(true);
      setError(null);

      await kubeconfigApi.refresh(id);

      setSuccess('Kubeconfig refreshed successfully');
      await loadKubeconfigs();
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Refresh failed');
    } finally {
      setLoading(false);
    }
  };

  const handleTest = async (id: string) => {
    try {
      setLoading(true);
      setError(null);

      const result = await kubeconfigApi.test(id);

      if (result.success) {
        setSuccess(result.message);
      } else {
        setError(result.message);
      }
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Test failed');
    } finally {
      setLoading(false);
    }
  };

  const handleView = async (kubeconfig: KubeconfigMetadata) => {
    try {
      setLoading(true);
      setError(null);

      const content = await kubeconfigApi.getContent(kubeconfig.id);
      setKubeconfigContent(content);
      setSelectedKubeconfig(kubeconfig);
      setViewDialogOpen(true);
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to load content');
    } finally {
      setLoading(false);
    }
  };

  const getSourceColor = (source: string) => {
    switch (source) {
      case 'rancher':
        return 'primary';
      case 'manual':
        return 'secondary';
      case 'eks':
      case 'gke':
      case 'aks':
        return 'info';
      default:
        return 'default';
    }
  };

  if (loading && kubeconfigs.length === 0) {
    return (
      <Box display="flex" justifyContent="center" p={3}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6">
          <SettingsIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          Kubeconfig Management
        </Typography>
        <Box>
          <Tooltip title="Refresh list">
            <IconButton onClick={loadKubeconfigs} disabled={loading}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Button
            variant="contained"
            startIcon={<CloudUploadIcon />}
            onClick={() => setUploadDialogOpen(true)}
            disabled={loading}
          >
            Upload Kubeconfig
          </Button>
        </Box>
      </Box>

      {/* Alerts */}
      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" onClose={() => setSuccess(null)} sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      {/* Kubeconfigs List */}
      {kubeconfigs.length === 0 ? (
        <Alert severity="info">
          No kubeconfigs found. Upload a kubeconfig or connect to Rancher to generate one.
        </Alert>
      ) : (
        kubeconfigs.map((kc) => (
          <Accordion key={kc.id} sx={{ mb: 1 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
                <Box display="flex" alignItems="center" gap={1}>
                  <Typography>{kc.name}</Typography>
                  <Chip label={kc.source} size="small" color={getSourceColor(kc.source)} />
                  {kc.contexts.length > 0 && (
                    <Chip
                      label={`${kc.contexts.length} context${kc.contexts.length > 1 ? 's' : ''}`}
                      size="small"
                      variant="outlined"
                    />
                  )}
                </Box>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">
                    <strong>ID:</strong> {kc.id}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Source:</strong> {kc.source}
                  </Typography>
                  {kc.rancherClusterId && (
                    <Typography variant="body2" color="text.secondary">
                      <strong>Rancher Cluster:</strong> {kc.rancherClusterName} ({kc.rancherClusterId})
                    </Typography>
                  )}
                  <Typography variant="body2" color="text.secondary">
                    <strong>File Path:</strong> {kc.filePath}
                  </Typography>
                  {kc.currentContext && (
                    <Typography variant="body2" color="text.secondary">
                      <strong>Current Context:</strong> {kc.currentContext}
                    </Typography>
                  )}
                  <Typography variant="body2" color="text.secondary">
                    <strong>Created:</strong> {new Date(kc.createdAt).toLocaleString()}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Updated:</strong> {new Date(kc.updatedAt).toLocaleString()}
                  </Typography>
                </Grid>

                {/* Contexts */}
                {kc.contexts.length > 0 && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" gutterBottom>
                      Contexts:
                    </Typography>
                    <List dense>
                      {kc.contexts.map((ctx, idx) => (
                        <ListItem key={idx}>
                          <ListItemText
                            primary={ctx.name}
                            secondary={`Cluster: ${ctx.cluster}, User: ${ctx.user}, Namespace: ${ctx.namespace || 'default'}`}
                          />
                          {ctx.name === kc.currentContext && (
                            <Chip label="Current" size="small" color="success" icon={<CheckCircleIcon />} />
                          )}
                        </ListItem>
                      ))}
                    </List>
                  </Grid>
                )}

                {/* Actions */}
                <Grid item xs={12}>
                  <Box display="flex" gap={1} flexWrap="wrap">
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<VisibilityIcon />}
                      onClick={() => handleView(kc)}
                      disabled={loading}
                    >
                      View Content
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => handleTest(kc.id)}
                      disabled={loading}
                    >
                      Test Connection
                    </Button>
                    {kc.source !== 'manual' && (
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<RefreshIcon />}
                        onClick={() => handleRefresh(kc.id)}
                        disabled={loading}
                      >
                        Refresh
                      </Button>
                    )}
                    <Button
                      size="small"
                      variant="outlined"
                      color="error"
                      startIcon={<DeleteIcon />}
                      onClick={() => handleDelete(kc.id)}
                      disabled={loading}
                    >
                      Delete
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>
        ))
      )}

      {/* Upload Dialog */}
      <Dialog
        open={uploadDialogOpen}
        onClose={() => !loading && setUploadDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Upload Kubeconfig</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Name"
                placeholder="Production Cluster"
                value={uploadForm.name}
                onChange={(e) => setUploadForm({ ...uploadForm, name: e.target.value })}
                required
                disabled={loading}
              />
            </Grid>
            <Grid item xs={12}>
              <ToggleButtonGroup
                value={uploadMode}
                exclusive
                onChange={(e, newMode) => newMode && setUploadMode(newMode)}
                fullWidth
              >
                <ToggleButton value="paste">Paste Content</ToggleButton>
                <ToggleButton value="file">Upload File</ToggleButton>
              </ToggleButtonGroup>
            </Grid>

            {uploadMode === 'paste' ? (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={12}
                  label="Kubeconfig Content"
                  placeholder="Paste your kubeconfig YAML here..."
                  value={uploadForm.kubeconfigContent}
                  onChange={(e) => setUploadForm({ ...uploadForm, kubeconfigContent: e.target.value })}
                  required
                  disabled={loading}
                  helperText="Raw YAML or base64 encoded"
                />
              </Grid>
            ) : (
              <Grid item xs={12}>
                <Button
                  fullWidth
                  component="label"
                  variant="outlined"
                  startIcon={<CloudUploadIcon />}
                  sx={{ py: 2 }}
                >
                  Select Kubeconfig File
                  <input
                    type="file"
                    hidden
                    accept=".yaml,.yml,.config,.txt"
                    onChange={handleFileUpload}
                  />
                </Button>
                {uploadForm.kubeconfigContent && (
                  <Alert severity="success" sx={{ mt: 2 }}>
                    File loaded successfully ({uploadForm.kubeconfigContent.length} characters)
                  </Alert>
                )}
              </Grid>
            )}
            <Grid item xs={12}>
              <Button
                fullWidth
                variant="outlined"
                onClick={handleValidate}
                disabled={loading || !uploadForm.kubeconfigContent}
              >
                Validate Kubeconfig
              </Button>
            </Grid>

            {/* Validation Results */}
            {validation && (
              <Grid item xs={12}>
                {validation.valid ? (
                  <Alert severity="success">
                    <Typography variant="body2">
                      <strong>Valid kubeconfig!</strong>
                    </Typography>
                    {validation.contexts.length > 0 && (
                      <>
                        <Typography variant="body2" sx={{ mt: 1 }}>
                          <strong>Contexts found:</strong>
                        </Typography>
                        <List dense>
                          {validation.contexts.map((ctx, idx) => (
                            <ListItem key={idx}>
                              <ListItemText
                                primary={ctx.name}
                                secondary={`${ctx.cluster} / ${ctx.user}`}
                              />
                            </ListItem>
                          ))}
                        </List>
                      </>
                    )}
                  </Alert>
                ) : (
                  <Alert severity="error">
                    <Typography variant="body2">
                      <strong>Validation errors:</strong>
                    </Typography>
                    <ul style={{ marginTop: 8, marginBottom: 0 }}>
                      {validation.errors.map((err, idx) => (
                        <li key={idx}>{err}</li>
                      ))}
                    </ul>
                  </Alert>
                )}
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setUploadDialogOpen(false); resetUploadForm(); }} disabled={loading}>
            Cancel
          </Button>
          <Button
            onClick={handleUpload}
            variant="contained"
            disabled={loading || !uploadForm.name || !uploadForm.kubeconfigContent}
          >
            {loading ? <CircularProgress size={24} /> : 'Upload'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Content Dialog */}
      <Dialog
        open={viewDialogOpen}
        onClose={() => setViewDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {selectedKubeconfig?.name} - Content
        </DialogTitle>
        <DialogContent dividers>
          <TextField
            fullWidth
            multiline
            rows={20}
            value={kubeconfigContent}
            InputProps={{
              readOnly: true,
              style: { fontFamily: 'monospace', fontSize: '0.875rem' },
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
